import { useEffect, useRef } from "react";
import { marked } from "marked";
import TurndownService from "turndown";
import { gfm } from "@joplin/turndown-plugin-gfm";

/**
 * Editor WYSIWYG minimalista para outputs de herramientas IA.
 *
 * - Se renderiza el markdown como HTML `contentEditable="true"` con exactamente
 *   la misma tipografía que la vista de lectura (`consulting-md`), incluyendo
 *   tablas visuales (no código plano).
 * - En cada `input`, convertimos HTML → markdown con `turndown` + plugin GFM
 *   (tablas, tachado, listas de tarea) y notificamos al padre vía `onChange`.
 * - No hay toolbar: el editor conserva el markdown existente y el usuario edita
 *   directamente el texto renderizado.
 *
 * Uso:
 *   <WysiwygMarkdownEditor initialMd={draft} onChange={setDraft} />
 *
 * `initialMd` se aplica UNA vez (al montar/re-crear la key). Para forzar reset,
 * cambia el `key` en el padre.
 */
const _turndown = (() => {
  const t = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
    hr: "---",
  });
  t.use(gfm);
  return t;
})();

export default function WysiwygMarkdownEditor({ initialMd = "", onChange, testid = "wysiwyg-editor" }) {
  const ref = useRef(null);
  const lastEmittedRef = useRef(initialMd);

  // Monta el HTML inicial una vez
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = marked.parse(initialMd || "", { async: false });
    lastEmittedRef.current = initialMd;
    // Sanea las tablas para que sean editables celda a celda (contentEditable
    // hereda del padre, así que basta con dejarlo).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleInput() {
    if (!ref.current) return;
    const md = _turndown.turndown(ref.current.innerHTML);
    if (md !== lastEmittedRef.current) {
      lastEmittedRef.current = md;
      onChange?.(md);
    }
  }

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      className="prose prose-slate max-w-none consulting-md wysiwyg-editor focus:outline-none"
      style={{
        minHeight: 420,
        borderRadius: 12,
        border: "1px solid #cbd5e1",
        background: "#fdfcf6",
        padding: "20px 24px",
        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)",
      }}
      data-testid={testid}
      spellCheck
    />
  );
}
